import { SupportedLanguage } from "@/services/languageService";

type CombinedMoveEffectDb = {
  version: number;
  updatedAt?: string;
  languages: Partial<Record<SupportedLanguage, Record<string, string>>>;
};

type PerLanguageMoveEffectDb = {
  version: number;
  updatedAt?: string;
  language: SupportedLanguage;
  effects: Record<string, string>;
};

const perLanguageCache = new Map<SupportedLanguage, Record<string, string>>();
const perLanguageInflight = new Map<SupportedLanguage, Promise<Record<string, string> | null>>();

let combinedCache: CombinedMoveEffectDb | null = null;
let combinedInflight: Promise<CombinedMoveEffectDb | null> | null = null;

const normalizeMoveKey = (moveName: string) => moveName.trim().toLowerCase();

async function loadCombinedDb(): Promise<CombinedMoveEffectDb | null> {
  if (combinedCache) return combinedCache;
  if (combinedInflight) return combinedInflight;

  combinedInflight = (async () => {
    try {
      const response = await fetch("/data/move-effect-translations.json", { cache: "no-store" });
      if (!response.ok) return null;
      const db = (await response.json()) as CombinedMoveEffectDb;
      if (!db || typeof db !== "object" || !db.languages) return null;
      combinedCache = db;

      if (import.meta.env?.DEV) {
        console.debug("[moveEffectTranslationDb] loaded languages:", Object.keys(db.languages || {}));
        const sample = db.languages?.["zh-CN"]?.["solar-beam"];
        console.debug("[moveEffectTranslationDb] sample zh-CN solar-beam:", sample ? "hit" : "miss");
      }

      return combinedCache;
    } catch {
      return null;
    } finally {
      combinedInflight = null;
    }
  })();

  return combinedInflight;
}

async function loadPerLanguageEffects(
  language: SupportedLanguage
): Promise<Record<string, string> | null> {
  const cached = perLanguageCache.get(language);
  if (cached) return cached;

  const inflight = perLanguageInflight.get(language);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      const response = await fetch(`/data/move-effect-translations.${language}.json`, { cache: "no-store" });
      if (!response.ok) return null;
      const db = (await response.json()) as PerLanguageMoveEffectDb;
      if (!db || typeof db !== "object" || db.language !== language || !db.effects) return null;
      perLanguageCache.set(language, db.effects);
      return db.effects;
    } catch {
      return null;
    } finally {
      perLanguageInflight.delete(language);
    }
  })();

  perLanguageInflight.set(language, promise);
  return promise;
}

export async function getLocalMoveEffectText(
  moveName: string,
  language: SupportedLanguage
): Promise<string | null> {
  const key = normalizeMoveKey(moveName);

  // 1) Try per-language file first (scales better for full DB)
  const perLang = await loadPerLanguageEffects(language);
  if (perLang && perLang[key]) {
    const value = perLang[key];
    const result = value && value.trim() ? value.trim() : null;
    if (result) return result;
  }

  // 2) Fallback to combined file (legacy / small overrides)
  const combined = await loadCombinedDb();
  const langBucket = combined?.languages?.[language];
  if (!langBucket) return null;

  const value = langBucket[key];
  const result = value && value.trim() ? value.trim() : null;

  if (import.meta.env?.DEV && !result) {
    console.debug(
      `[moveEffectTranslationDb] miss: move=${key} language=${language}`
    );
  }

  return result;
}

